import { useContext, useEffect } from "react";
import axios from "axios";
import { endPoints, MIDDLEWARE_SLUG } from "../apis/apiEndPoints";
import { ToastContext } from "../utils/context/toastContext";
import { getErrorMsg } from "../utils/whatsAppUtils";

const POLLING_INTERVAL = 5000; // 5 seconds

const useDownloadWhatsAppReplies = (requestId: string) => {
  const { setToastValue } = useContext(ToastContext);

  useEffect(() => {
    let intervalId: ReturnType<typeof setInterval>;
    const fetchReplies = async () => {
      try {
        if (!requestId) return;

        const downloadUrl = `${window.location.origin}${MIDDLEWARE_SLUG}/middleware/${endPoints.DOWNLOAD_WHATS_APP_REPLIES}?requestId=${requestId}`;

        const response = await axios({
          url: downloadUrl,
          responseType: "blob",
        });

        // Check the content type
        const contentType = response.headers["content-type"];

        if (contentType.includes("application/json")) {
          // Parse JSON response
          const textData = await response.data.text();
          const jsonData = JSON.parse(textData);

          if (jsonData.continuePolling) {
            // Continue polling
            return;
          } else {
            // Stop polling when continuePolling is false
            clearInterval(intervalId);
          }
        } else if (contentType.includes("application/octet-stream")) {
          // Handle blob response (file download)
          const href = URL.createObjectURL(response.data);
          const link = document.createElement("a");
          link.href = href;
          link.setAttribute("download", "whatsAppReplies.xls");
          link.click();

          URL.revokeObjectURL(href);

          // Stop polling after file is downloaded
          clearInterval(intervalId);
          setToastValue({
            isToastShown: true,
            toastMessage: "File downloaded successfully.",
            toastKey: "uploadImageError",
            toastSeverity: "success",
          });
        }
      } catch (error) {
        clearInterval(intervalId);
        setToastValue({
          isToastShown: true,
          toastMessage: getErrorMsg({
            error,
            defaultMsg: "Failed to download the file. Please try again.",
          }),
          toastKey: "uploadImageError",
          toastSeverity: "error",
        });
      }
    };

    if (requestId) {
      setToastValue({
        isToastShown: true,
        toastMessage:
          "Your file is being prepared for download. Please wait a moment.",
        toastKey: "uploadImageError",
        toastSeverity: "info",
      });
      intervalId = setInterval(fetchReplies, POLLING_INTERVAL);
      fetchReplies();
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [requestId, setToastValue]);
};

export default useDownloadWhatsAppReplies;
